/**
 * Real session-facts resolver (C1, #22323) — replaces the interim
 * dev-bypass-only `dev_session_facts.ts` (deleted; its entity-context piece
 * survived for a while as `dev_entity_context.ts` for the not-yet-migrated
 * `dev_*.ts` resolvers, until C12 (#22334) deleted that module too, along
 * with every `dev_*.ts` resolver it served). Self-fetching hook, NOT a
 * `dev_*.ts`/boot-time seed — see `use_my_pay.ts`'s module docblock for the
 * same "why": this is real production OAuth-gated auth work, live in every
 * environment (including production), not just local dev.
 *
 * Uses the real bearer client (`getTrpcClient()`, `lib/trpc.ts`) and the
 * shared `useOAuthEntityContext()` helper (`oauth_entity_context.ts`) —
 * never `firm.list`/`firm.create`/`entity.list`. Two concurrent calls:
 * `entityUser.me({ firm_id, entity_id })` for `perms.payroll_role` +
 * `is_adviser` + `perms.manage_users`, and `entity.clientGet({ entity_id })`
 * for `entity.name` as `company_name`. Both are on the OAuth allowlist gated
 * behind `payroll:employer:read`/`payroll:employer:write`
 * (`apps/api/src/trpc/oauth_allowlist.ts`).
 *
 * ── The organisation-settings read is SHARED, never re-fetched (C11,
 *    #22333) ──────────────────────────────────────────────────────────────
 *
 * `entity.clientGet`'s response already carries `financial_year_end` and
 * `currency_code` alongside `name` — this module already fetches the whole
 * row for `company_name`, it just used to discard the other two fields.
 * `resolveOrganisationRecord` below narrows the SAME `clientResult.entity`
 * this hook already has in hand into a `RealOrganisationRecord`
 * (`wagepro_store_settings.ts`'s adapter shape), and `main.tsx` threads it
 * into `WageProStoreProvider`'s `organisation_record` prop. This is the ONE
 * `entity.clientGet` round trip the whole app makes — `use_settings.ts`'s
 * real save path (`saveRealOrganisationSettings`) never re-reads via
 * `clientGet`, only writes via `entity.update`.
 *
 * There is NO mock fallback here — same stance as `use_my_pay.ts`: a mock
 * role/company shown to an admin as their own real session would be
 * fabricated identity data. Every failure surfaces as a real
 * `SessionFactsResult` (`no_entity`/`error`) instead. `main.tsx`'s
 * `AuthGate` gates mounting `<WageProStoreProvider>`/`<App>` on this state
 * being `ready` — a `no_entity` grant (an OAuth token with no bound entity)
 * renders an explicit "no payroll access" screen, never a session built from
 * `MOCK_SESSION`. This claim previously had a real gap (fixed 2026-07, PR
 * #22341 adversarial review finding #3): `deriveRole`
 * (`wagepro_store_session_facts.ts`) used to fall back to the CALLER's
 * pre-existing role — `MOCK_SESSION.role === 'admin'` at every un-migrated
 * call site — whenever `payroll_role === 'none'`, which is every real
 * non-adviser membership today (see `deriveRole`'s docblock). `manage_users`
 * is now read from the SAME `entityUser.me` response (no extra round trip)
 * so that fallback derives from a real entity permission instead.
 *
 * `deriveSessionFromFacts` (`wagepro_store_session_facts.ts`) is the
 * permanent adapter that turns a resolved `SessionFacts` into the session's
 * `role`/`company_name`/`tint` — unchanged by this pass; only HOW
 * `SessionFacts` gets resolved changes here.
 */
import * as React from 'react';
import { getTrpcClient } from '../lib/trpc.js';
import {
  useOAuthEntityContext,
  type OAuthEntityContext,
} from '../lib/auth/oauth_entity_context.js';
import type { SessionFacts } from './wagepro_store_session_facts.js';
import type { RealOrganisationRecord } from './wagepro_store_settings.js';

export type SessionFactsResult =
  | { status: 'loading' }
  | { status: 'no_entity' }
  | {
      status: 'ready';
      facts: SessionFacts;
      organisation: RealOrganisationRecord;
    }
  | { status: 'error' };

/** The exact subset of `entity.clientGet`'s response this hook already has in hand, narrowed to the Settings screen's adapter shape — see module docblock. */
function resolveOrganisationRecord(entity: {
  name: string;
  financial_year_end: number;
  currency_code: string;
}): RealOrganisationRecord {
  return {
    name: entity.name,
    financial_year_end: entity.financial_year_end,
    currency_code: entity.currency_code,
  };
}

export interface UseSessionFactsResult {
  state: SessionFactsResult;
  /** Re-runs both fetches from scratch. Only meaningful for the `error` state. */
  retry: () => void;
}

/**
 * Pure derivation, exported separately from the hook below so it is
 * unit-testable without rendering React (mirrors `resolveMyPayState` /
 * `resolveAuthBoot`'s "pure decision function + thin hook" split).
 *
 * Priority: `no_entity` always wins (a caller whose grant just lost its
 * entity binding must see `no_entity`, not a leftover `error` from before
 * the context changed) — then resolved `facts`+`organisation` win over a
 * `failed` flag (a successful retry's facts must display even if a stale
 * `failed` flag from an earlier attempt was never explicitly cleared by the
 * caller) — then `failed` — then `loading`. In the real hook, `facts` and
 * `organisation` are always set together (same `Promise.all` resolution,
 * same `.then()`) and reset together with `failed` at the start of every
 * fetch, so this function requires BOTH before reporting `ready` (C11,
 * #22333) — this is not just defensive: `organisation` (C11's shared
 * `entity.clientGet` reuse) is the guard `resolveOrganisationActions`
 * (`use_settings.ts`) needs to know the real read genuinely succeeded before
 * ever wiring a real save mutation over it.
 */
export function resolveSessionFactsState(input: {
  entityContext: OAuthEntityContext;
  facts: SessionFacts | undefined;
  organisation: RealOrganisationRecord | undefined;
  failed: boolean;
}): SessionFactsResult {
  if (input.entityContext.status === 'no_entity') {
    return { status: 'no_entity' };
  }
  if (input.facts !== undefined && input.organisation !== undefined) {
    return {
      status: 'ready',
      facts: input.facts,
      organisation: input.organisation,
    };
  }
  if (input.failed) return { status: 'error' };
  return { status: 'loading' };
}

/** The signed-in admin's real `payroll_role`/`is_adviser`/`company_name` facts. */
export function useSessionFacts(): UseSessionFactsResult {
  const entityContext = useOAuthEntityContext();
  const [facts, setFacts] = React.useState<SessionFacts | undefined>(undefined);
  const [organisation, setOrganisation] = React.useState<
    RealOrganisationRecord | undefined
  >(undefined);
  const [failed, setFailed] = React.useState(false);
  const [reloadToken, setReloadToken] = React.useState(0);

  React.useEffect(() => {
    if (entityContext.status !== 'ready') return;
    // Captured as its own consts so the nested async closure below
    // references definitely-string values rather than relying on narrowing
    // of `entityContext` (a hook return value) surviving across the closure
    // boundary -- same pattern `use_my_pay.ts` uses for `selectedEntityId`.
    const firmId: string = entityContext.firm_id;
    const entityId: string = entityContext.entity_id;
    let cancelled = false;
    setFacts(undefined);
    setOrganisation(undefined);
    setFailed(false);

    async function load(): Promise<void> {
      try {
        const client = getTrpcClient();
        const [meResult, clientResult] = await Promise.all([
          client.entityUser.me.query({ firm_id: firmId, entity_id: entityId }),
          client.entity.clientGet.query({ entity_id: entityId }),
        ]);
        if (cancelled) return;
        setFacts({
          payroll_role: meResult.perms.payroll_role,
          is_adviser: meResult.is_adviser,
          manage_users: meResult.perms.manage_users,
          entity_role: meResult.role,
          firm_role: meResult.firm_role,
          company_name: clientResult.entity.name,
        });
        // Same `clientResult.entity` this call already fetched for
        // `company_name` above -- see module docblock's "shared, never
        // re-fetched" note (C11, #22333).
        setOrganisation(resolveOrganisationRecord(clientResult.entity));
      } catch (err) {
        if (cancelled) return;
        console.error(
          '[wagepro-web] Failed to resolve real session facts:',
          err,
        );
        setFailed(true);
      }
    }

    void load();
    return () => {
      cancelled = true;
    };
  }, [entityContext, reloadToken]);

  const state = resolveSessionFactsState({
    entityContext,
    facts,
    organisation,
    failed,
  });

  // Stable across renders (`useCallback` with no deps, functional state
  // updater) -- not just tidiness. `main.tsx`'s `AuthGate` needs to pass this
  // into a `useMemo` (`resolveOrganisationActions`, C11 #22333 second-round
  // adversarial review) without a fresh closure identity defeating that
  // memo's whole point on every render, the same render-churn class of bug
  // the memo itself was added to fix.
  const retry = React.useCallback(() => {
    setReloadToken((token) => token + 1);
  }, []);

  return { state, retry };
}
